/**
 * Parse the SSE response from /api/aria/chat.
 *
 * The server streams `: ka` keep-alive comments (ignored) and emits ONE
 * `data: <json>\n\n` event at the end. We accumulate the body, scan for
 * the last `data: ` line, and parse its JSON.
 *
 * Why a helper:
 *   · K8s ingress 60s timeout — keep-alives defeat it.
 *   · Same logic used by Monetize chat + Execution QuestionDrawer chat.
 *   · Frontend stays stupid: still one fetch → one JSON result.
 */
export async function fetchAriaChat(api, payload) {
  const r = await fetch(`${api}/api/aria/chat`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    return { ok: false, status: r.status, error: `HTTP ${r.status}` };
  }
  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let final = null;
  const tryParseChunk = (chunk) => {
    if (!chunk || chunk.startsWith(':')) return;
    const m = chunk.match(/^data:\s*([\s\S]*)$/);
    if (m) {
      try { final = JSON.parse(m[1]); } catch { /* ignore */ }
    }
  };
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
    let idx;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      tryParseChunk(buffer.slice(0, idx));
      buffer = buffer.slice(idx + 2);
    }
  }
  // Flush any trailing chunk that didn't end with \n\n (some proxies trim it).
  if (buffer.trim()) tryParseChunk(buffer.trim());
  return final || { ok: false, error: 'empty stream' };
}
